import type { Anomaly, ChatTo, GameState, GuestIntent, Meeting, ShowState } from '@continuum/show-core';
import type { Session } from './session.js';

/** Mirrors apps/worker/src/protocol.ts. */
export type WireSeat = Omit<ShowState['seats'][number], 'token' | 'alignment' | 'lastSeenAt' | 'volunteer'>;
export type WireAnomaly = Omit<Anomaly, 'source' | 'solved' | 'by'> & {
  /** How many of those handed FIX have solved their puzzle — never who. Absent from a room before the puzzle. */
  fixes?: number;
};
export type WireMeeting = Meeting & { ballots?: number };
export type WireGame = Omit<GameState, 'sabotagesUsed' | 'rogue' | 'rogueFired' | 'agentStops' | 'armed' | 'drawSeed' | 'anomalies' | 'meeting'> & {
  anomalies: WireAnomaly[];
  meeting: WireMeeting | null;
};
export type WireState = Omit<ShowState, 'seats' | 'log' | 'taps' | 'game' | 'seeds'> & {
  seats: WireSeat[];
  game: WireGame;
};

export interface You {
  seat: number;
  callsign: string | null;
  profession: ShowState['seats'][number]['profession'];
  alignment: ShowState['seats'][number]['alignment'];
  volunteer: boolean | null;
  brig: boolean;
  sabotagesLeft: number;
  ballot: string | null;
  /** The open alarms this guest has solved their FIX puzzle for. Absent from a room before the puzzle. */
  solved?: string[];
  /** Whether this guest has found the coasters' hidden signal tonight (show-core `signal.ts`). Absent from an older room. */
  signal?: boolean;
  /** A saboteur's game of the stop, the sabotage, is played (show-core `stop.ts`). Absent from an older room. */
  armed?: boolean;
  /** The eras this guest was a saboteur in, for their own evening at the end. Absent from an older room. */
  saboteurAt?: number[];
}

export type ConnectionStatus = 'connecting' | 'open' | 'closed';

/**
 * Why the room will not let this phone in, when the phone can tell. The room refuses the socket
 * before it opens, and a browser does not show why, so the phone asks two plain questions:
 * is the room there at all, and is this seat code still the seat's current one.
 * - `replaced`: the captain checked this guest out or moved them;
 * - `expired`: the code is for another evening, or broken;
 * - `closed`: the code is fine, so the door is shut (not open yet, or the evening is over).
 */
export type Refusal = 'replaced' | 'expired' | 'closed';

export interface RoomEvents {
  onState(state: WireState, you: You | null): void;
  onPatch(patch: Partial<WireState>): void;
  onYou(you: You): void;
  onCue(name: string): void;
  onStatus(status: ConnectionStatus): void;
  /** The room turned a message down; `intent` is which kind, when the phone knows. */
  onRejected(error: string, intent: string | null): void;
  /** The room will not let this phone in (null once it does). */
  onRefused(reason: Refusal | null): void;
  /** The steward moved this guest: rejoin on the new seat with this pass. */
  onMoved(session: Session): void;
  /** The steward checked this guest out. */
  onFreed(): void;
  /**
   * Messages between guests (chat/): this phone's own view, a new one, someone having read one, or
   * the room's answer to one this phone sent. They never ride the shared state.
   */
  onChat?(msg: Record<string, unknown>): void;
  /** The TV follows this guest's game, or stops (show-core `boardFeed.ts`): send the board while it does. */
  onShowBoard?(on: boolean): void;
}

const BACKOFF_MS = [500, 1_000, 2_000, 4_000, 8_000, 15_000];
const MAX_RECLAIMS = 3;
const HEARTBEAT_MS = 20_000;
/** How long a tap made offline is still worth sending, and how many are kept. */
const OUTBOX_MS = 20_000;
const OUTBOX_MAX = 20;
/**
 * A link is checked, not trusted (2026-10-09: the venue's Wi-Fi dropped every phone at once, and a phone
 * back from the lock screen sat on a socket that had died in its pocket, waiting out a backoff of up to
 * 15 s). Waking, coming back online, or hearing nothing for `QUIET_MS` while in view, the phone asks
 * "ping" — the room answers "pong" without waking — and a link with no answer in `ANSWER_MS` is dialled
 * again at once.
 */
export const QUIET_MS = 30_000;
export const ANSWER_MS = 4_000;
/** A dial that has not opened in this long is given up and tried again. */
const DIAL_MS = 8_000;

/**
 * The phone's socket into the VoyageRoom. Reconnects on its own with backoff and
 * re-presents the seat token, so a locked phone rejoins without the guest noticing.
 */
export class RoomClient {
  private ws: WebSocket | null = null;
  private attempt = 0;
  /** Silent re-claims on this connection, so a genuinely dead seat still surfaces. */
  private reclaims = 0;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private closed = false;
  /** Connections in a row that never opened: two of those and the phone asks why. */
  private failedOpens = 0;
  private probing = false;
  /** Every message carries an id; the cloud room names it in its answer, so a refusal is matched to what it refused. */
  private nextId = 1;
  private sent = new Map<number, string>();
  /** For a room that does not echo ids (the Mac's local one): the last thing sent, and when. */
  private last: { type: string; at: number } | null = null;
  /** Ids of the messages to other guests still waiting for an answer; both rooms echo these. */
  private chats = new Set<number>();
  /** When the room was last heard from, anything at all, a "pong" included. */
  private lastHeardAt = 0;
  private dialledAt = 0;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private watcher: ReturnType<typeof setInterval> | null = null;
  private answerWait: ReturnType<typeof setTimeout> | null = null;
  private readonly wake = () => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') this.checkLink();
  };

  constructor(
    private readonly origin: string,
    private readonly session: Session,
    private readonly events: RoomEvents,
    /** How long a "ping" waits for its "pong" (shorter in the tests). */
    private readonly answerMs = ANSWER_MS,
  ) {}

  /**
   * Taps made while the line was down: a pocketed phone, a lift, a wobbly signal. They go out
   * as soon as the seat is claimed again, instead of vanishing and leaving a screen waiting for
   * an answer that was never asked for. Old ones are dropped, so nothing replays minutes later.
   */
  private outbox: { intent: GuestIntent; at: number }[] = [];

  connect(): void {
    this.closed = false;
    if (typeof window !== 'undefined') {
      document.addEventListener('visibilitychange', this.wake);
      window.addEventListener('online', this.wake);
      window.addEventListener('pageshow', this.wake);
      this.watcher ??= setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        const open = this.ws?.readyState === WebSocket.OPEN;
        if ((open && Date.now() - this.lastHeardAt > QUIET_MS) || (!open && this.ws?.readyState === WebSocket.CONNECTING && Date.now() - this.dialledAt > DIAL_MS)) this.checkLink();
      }, 5_000);
    }
    this.open();
  }

  /**
   * Is the link alive? Open: ask "ping", and dial again if nothing comes back. Waiting out a backoff, or
   * stuck dialling: dial now. Called on waking, on coming back online, and when the room has gone quiet.
   */
  checkLink(): void {
    if (this.closed) return;
    const ws = this.ws;
    if (ws?.readyState === WebSocket.OPEN) {
      if (this.answerWait) return;
      const asked = Date.now();
      try {
        ws.send('ping');
      } catch {
        this.redial();
        return;
      }
      this.answerWait = setTimeout(() => {
        this.answerWait = null;
        if (this.ws === ws && this.lastHeardAt < asked) this.redial();
      }, this.answerMs);
      return;
    }
    if (ws?.readyState === WebSocket.CONNECTING && Date.now() - this.dialledAt < DIAL_MS) return;
    this.redial();
  }

  /** Drop whatever socket there is, quietly, and dial at once with the backoff started over. */
  private redial(): void {
    if (this.closed) return;
    if (this.retry) clearTimeout(this.retry);
    this.retry = null;
    this.stopHeartbeat();
    const old = this.ws;
    this.ws = null;
    try {
      old?.close();
    } catch {
      /* already gone */
    }
    this.attempt = 0;
    this.open();
  }

  private open(): void {
    const base = this.origin.replace(/^http/, 'ws').replace(/\/$/, '');
    // The LAN fallback serves the app and the room from one origin on :8787.
    const path = this.origin.includes(':8787')
      ? '/ws'
      : `/api/voyage/${encodeURIComponent(this.session.voyageId)}/ws`;
    const url = `${base}${path}?t=${encodeURIComponent(this.session.token)}`;
    this.events.onStatus('connecting');

    const ws = new WebSocket(url);
    this.ws = ws;
    this.dialledAt = Date.now();
    let opened = false;

    ws.onopen = () => {
      // A socket dropped since (a redial) says nothing more: never two of them claiming the seat.
      if (this.ws !== ws) return;
      opened = true;
      this.lastHeardAt = Date.now();
      this.attempt = 0;
      this.reclaims = 0;
      if (this.failedOpens > 0) this.events.onRefused(null);
      this.failedOpens = 0;
      this.events.onStatus('open');
      // A new socket shows no board until the room says the TV follows it again.
      this.events.onShowBoard?.(false);
      // Re-present the seat token; the room treats a known token as a rejoin.
      this.send({ type: 'claimSeat', seat: this.session.seat, token: this.session.token });
      this.stopHeartbeat();
      this.heartbeat = setInterval(() => this.send({ type: 'heartbeat' }), HEARTBEAT_MS);
      // The room takes messages in arrival order, so these land after the claim.
      const waiting = this.outbox.filter((entry) => Date.now() - entry.at < OUTBOX_MS);
      this.outbox = [];
      for (const entry of waiting) this.send(entry.intent);
    };

    ws.onmessage = (event: MessageEvent<string>) => {
      if (this.ws !== ws) return;
      this.lastHeardAt = Date.now();
      if (event.data === 'pong') return;
      let msg: Record<string, unknown>;
      try {
        msg = JSON.parse(event.data) as Record<string, unknown>;
      } catch {
        return;
      }
      const chatAnswer = (msg.t === 'ack' || msg.t === 'nack') && typeof msg.id === 'number' && this.chats.delete(msg.id);
      if (msg.t === 'chatView' || msg.t === 'chat' || msg.t === 'chatSeen' || chatAnswer) {
        this.events.onChat?.(msg);
        // The claim went with a room reset: claim again, as for any tap (below), and say it did not go.
        if (msg.t === 'nack' && msg.error === 'seat not claimed' && this.reclaims < MAX_RECLAIMS) {
          this.reclaims += 1;
          this.send({ type: 'claimSeat', seat: this.session.seat, token: this.session.token });
        }
        return;
      }
      switch (msg.t) {
        case 'state':
          this.events.onState(msg.state as WireState, (msg.you as You | null) ?? null);
          break;
        case 'patch':
          this.events.onPatch(msg.patch as Partial<WireState>);
          break;
        case 'you':
          this.events.onYou(msg.you as You);
          break;
        case 'cue':
          this.events.onCue(String(msg.name));
          break;
        case 'showBoard':
          this.events.onShowBoard?.(msg.on === true);
          break;
        case 'moved':
          // This connection is finished; the app opens a new one on the new seat.
          this.closed = true;
          this.events.onMoved({ voyageId: String(msg.voyageId), seat: Number(msg.seat), token: String(msg.token) });
          break;
        case 'freed':
          this.closed = true;
          this.events.onFreed();
          break;
        case 'ack':
          if (typeof msg.id === 'number') this.sent.delete(msg.id);
          break;
        case 'nack': {
          const error = String(msg.error);
          const id = typeof msg.id === 'number' ? msg.id : null;
          const intent =
            id !== null
              ? (this.sent.get(id) ?? null)
              : this.last && Date.now() - this.last.at < 3_000
                ? this.last.type
                : null;
          if (id !== null) this.sent.delete(id);
          // "seat not claimed" means the room no longer has our token against this
          // seat, while our socket is still perfectly open — a room reset does exactly
          // that. Nothing re-sends the claim on a live socket, so without this the
          // phone is stuck rejecting every tap until the guest thinks to reload, which
          // at a dinner table they will not. Re-present the token and say nothing.
          if (error === 'seat not claimed' && this.reclaims < MAX_RECLAIMS) {
            this.reclaims += 1;
            this.send({ type: 'claimSeat', seat: this.session.seat, token: this.session.token });
            return;
          }
          this.events.onRejected(error, intent);
          break;
        }
        default:
          break;
      }
    };

    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.stopHeartbeat();
      // A client that was closed on purpose (a new seat, a new page) says nothing more: its late
      // "closed" would otherwise land after the new connection's "open" and show a false alarm.
      if (this.closed) return;
      this.events.onStatus('closed');
      if (!opened) {
        this.failedOpens += 1;
        if (this.failedOpens >= 2) void this.probe();
      }
      const wait = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)] ?? 15_000;
      this.attempt += 1;
      if (this.retry) clearTimeout(this.retry);
      this.retry = setTimeout(() => {
        this.retry = null;
        if (!this.closed) this.open();
      }, wait);
    };

    ws.onerror = () => ws.close();
  }

  send(intent: GuestIntent): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const id = this.nextId++;
      if (intent.type !== 'heartbeat') {
        this.sent.set(id, intent.type);
        this.last = { type: intent.type, at: Date.now() };
        // Answers to old messages are not waited on for ever.
        if (this.sent.size > 60) this.sent.delete(this.sent.keys().next().value as number);
      }
      this.ws.send(JSON.stringify({ id, intent }));
      return;
    }
    // The claim and the heartbeat are sent afresh on every connect; everything else waits.
    if (intent.type === 'claimSeat' || intent.type === 'heartbeat') return;
    this.outbox.push({ intent, at: Date.now() });
    if (this.outbox.length > OUTBOX_MAX) this.outbox.shift();
  }

  /**
   * A look at this guest's board for the TV, while the room says it follows them: beside the show,
   * never kept, dropped when offline.
   */
  sendBoard(view: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ board: view }));
  }

  /**
   * A message to another guest, or to the saboteurs' channel. Unlike a tap it is never kept for
   * later: offline it returns null at once, and the phone says it did not go.
   */
  sendChat(to: ChatTo, text: string): number | null {
    if (this.ws?.readyState !== WebSocket.OPEN) return null;
    const id = this.nextId++;
    this.chats.add(id);
    if (this.chats.size > 60) this.chats.delete(this.chats.values().next().value as number);
    this.ws.send(JSON.stringify({ id, chat: { to, text } }));
    return id;
  }

  /** How far this guest has read a conversation, for the others' Read. Dropped while offline: the next join says it again. */
  sendChatRead(to: ChatTo, n: number): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ chatRead: { to, n } }));
  }

  /**
   * The socket keeps failing before it opens. Is the room there, and is this seat code still
   * good? Only the cloud room answers these; on the Mac's local room nothing is asked.
   */
  private async probe(): Promise<void> {
    if (this.probing || this.origin.includes(':8787')) return;
    this.probing = true;
    try {
      const base = this.origin.replace(/\/$/, '');
      const health = await fetch(`${base}/api/health`, { cache: 'no-store' });
      if (!health.ok) return;
      const query = new URLSearchParams({
        seat: String(this.session.seat),
        voyage: this.session.voyageId,
        t: this.session.token,
      });
      const answer = await fetch(`${base}/board?${query.toString()}`, { redirect: 'manual', cache: 'no-store' });
      let reason: Refusal | null = null;
      if (answer.type === 'opaqueredirect' || (answer.status >= 300 && answer.status < 400)) reason = 'closed';
      else if (answer.status === 403) reason = 'replaced';
      else if (answer.status === 401) reason = 'expired';
      if (reason && !this.closed && this.failedOpens > 0) this.events.onRefused(reason);
    } catch {
      /* no network at all: that is "offline", not a refusal */
    } finally {
      this.probing = false;
    }
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== null) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }

  close(): void {
    this.closed = true;
    this.stopHeartbeat();
    if (this.retry) clearTimeout(this.retry);
    if (this.watcher) clearInterval(this.watcher);
    if (this.answerWait) clearTimeout(this.answerWait);
    this.retry = this.watcher = this.answerWait = null;
    if (typeof window !== 'undefined') {
      document.removeEventListener('visibilitychange', this.wake);
      window.removeEventListener('online', this.wake);
      window.removeEventListener('pageshow', this.wake);
    }
    this.ws?.close();
  }
}
